import React, { useState, useEffect } from 'react';
import { Phone, Search, Loader2, Mic, Laptop, Video } from 'lucide-react';
import { apiCall } from '../../services/api';

const NewCallModal = ({ 
  isOpen, 
  onClose, 
  contactsList, 
  loadingContacts, 
  fetchICloudContacts, 
  onCallInitiated, 
  voiceMakeCall, 
  voiceReady 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const [callingContact, setCallingContact] = useState(null);
  const [callError, setCallError] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isCallingManual, setIsCallingManual] = useState(false);
  const [callMode, setCallMode] = useState('mac-phone'); // Default to Mac Phone
  const [macPhoneType, setMacPhoneType] = useState('personal');

  useEffect(() => {
    if (isOpen && initialLoad) {
      fetchICloudContacts();
      setInitialLoad(false);
      setSearchQuery('');
      setManualNumber('');
      setCallError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setInitialLoad(true);
      setCallingContact(null);
      setIsCallingManual(false);
    }
  }, [isOpen]);

  const filteredContacts = contactsList
    .filter(contact => {
      if (!contact.phone) return false;
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        contact.name?.toLowerCase().includes(query) ||
        contact.phone?.toLowerCase().includes(query) ||
        contact.email?.toLowerCase().includes(query)
      );
    })
    .slice(0, 50);

  const handleCall = async (contact) => {
    if (!contact.phone) {
      setCallError('This contact has no phone number');
      return;
    }
    
    setCallingContact(contact);
    setCallError(null);
    
    try {
      // Mac-based calls (phone, FaceTime video, FaceTime audio)
      if (callMode.startsWith('mac-')) {
        const callType = callMode === 'mac-phone' ? 'phone' : 
                        callMode === 'mac-facetime' ? 'facetime' : 'facetime-audio';
        
        const response = await apiCall('/mac-calls/queue', {
          method: 'POST',
          body: JSON.stringify({
            recipient: contact.phone,
            recipient_name: contact.name,
            call_type: callType,
            phone_type: macPhoneType
          })
        });
        
        onCallInitiated({
          name: contact.name,
          phone: contact.phone,
          avatar: contact.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&size=128&background=random&color=fff&bold=true`,
          isMacCall: true,
          callType: callType,
          callId: response.call_id
        });
        
        setCallError(null);
        setCallingContact(null);
      } else if (voiceReady && voiceMakeCall && callMode === 'browser') {
        await voiceMakeCall(contact.phone);
        onCallInitiated({
          name: contact.name,
          phone: contact.phone,
          avatar: contact.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&size=128&background=random&color=fff&bold=true`,
          isBrowserCall: true,
          isFaceTime: false
        });
      } else {
        const response = await apiCall('/calls/make-call', {
          method: 'POST',
          body: JSON.stringify({
            to_phone: contact.phone,
            recipient_name: contact.name
          })
        });
        onCallInitiated({
          name: contact.name,
          phone: contact.phone,
          avatar: contact.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&size=128&background=random&color=fff&bold=true`,
          callSid: response.call_sid,
          isFaceTime: false
        });
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      setCallError(error.message || 'Failed to initiate call');
      setCallingContact(null);
    }
  };

  const handleManualCall = async () => {
    const phoneNumber = manualNumber.trim();
    if (!phoneNumber) {
      setCallError('Please enter a phone number');
      return;
    }
    
    const cleanNumber = phoneNumber.replace(/[-\s()]/g, '');
    if (!/^\+?[0-9]{10,15}$/.test(cleanNumber)) {
      setCallError('Please enter a valid phone number');
      return;
    }
    
    setIsCallingManual(true);
    setCallError(null);
    
    try {
      // Mac-based calls
      if (callMode.startsWith('mac-')) {
        const callType = callMode === 'mac-phone' ? 'phone' : 
                        callMode === 'mac-facetime' ? 'facetime' : 'facetime-audio';
        
        const response = await apiCall('/mac-calls/queue', {
          method: 'POST',
          body: JSON.stringify({
            recipient: phoneNumber,
            recipient_name: phoneNumber,
            call_type: callType,
            phone_type: macPhoneType
          })
        });
        
        onCallInitiated({
          name: phoneNumber,
          phone: phoneNumber,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(phoneNumber.slice(-4))}&size=128&background=22c55e&color=fff&bold=true`,
          isMacCall: true,
          callType: callType,
          callId: response.call_id
        });
      } else if (voiceReady && voiceMakeCall && callMode === 'browser') {
        await voiceMakeCall(phoneNumber);
        onCallInitiated({
          name: phoneNumber,
          phone: phoneNumber,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(phoneNumber.slice(-4))}&size=128&background=22c55e&color=fff&bold=true`,
          isBrowserCall: true,
          isFaceTime: false
        });
      } else {
        const response = await apiCall('/calls/make-call', {
          method: 'POST',
          body: JSON.stringify({
            to_phone: phoneNumber,
            recipient_name: phoneNumber
          })
        });
        onCallInitiated({
          name: phoneNumber,
          phone: phoneNumber,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(phoneNumber.slice(-4))}&size=128&background=22c55e&color=fff&bold=true`,
          callSid: response.call_sid,
          isFaceTime: false
        });
      }
    } catch (error) {
      console.error('Error initiating manual call:', error);
      setCallError(error.message || 'Failed to initiate call');
      setIsCallingManual(false);
    }
  };

  const handleManualKeyPress = (e) => {
    if (e.key === 'Enter' && manualNumber.trim()) {
      handleManualCall();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[500px] max-h-[750px] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-400/20">
          <h2 className="text-2xl font-bold text-white">New Call</h2>
          <p className="text-gray-400 text-sm mt-1">Dial a number or select a contact to call</p>
          
          {/* Call Mode Selection */}
          <div className="mt-4 space-y-3">
            {/* Mac Call Options - Always show these as primary */}
            <div className="p-3 bg-gray-800 rounded-lg">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Call via Your Mac
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCallMode('mac-phone')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    callMode === 'mac-phone' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
                  }`}
                  data-testid="mac-phone-btn"
                >
                  <Phone size={16} />
                  Phone
                </button>
                <button
                  onClick={() => setCallMode('mac-facetime')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    callMode === 'mac-facetime' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
                  }`}
                  data-testid="mac-facetime-btn"
                >
                  <Video size={16} />
                  FaceTime
                </button>
                <button
                  onClick={() => setCallMode('mac-facetime-audio')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    callMode === 'mac-facetime-audio' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
                  }`}
                  data-testid="mac-facetime-audio-btn"
                >
                  <Mic size={16} />
                  FT Audio
                </button>
              </div>
              
              {/* Phone Type Selection for Mac Calls */}
              {callMode.startsWith('mac-') && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-gray-500">Use:</span>
                  <button
                    onClick={() => setMacPhoneType('personal')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      macPhoneType === 'personal' 
                        ? 'bg-green-600/20 text-green-400 border border-green-600' 
                        : 'bg-gray-700 text-gray-400 hover:text-white'
                    }`}
                  >
                    Personal Mac
                  </button>
                  <button
                    onClick={() => setMacPhoneType('business')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      macPhoneType === 'business' 
                        ? 'bg-blue-600/20 text-blue-400 border border-blue-600' 
                        : 'bg-gray-700 text-gray-400 hover:text-white'
                    }`}
                  >
                    Business Mac
                  </button>
                </div>
              )}
            </div>
            
            {/* Other Call Options */}
            {voiceReady && (
              <div className="p-3 bg-gray-800/50 rounded-lg">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Other Options
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCallMode('browser')}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      callMode === 'browser' 
                        ? 'bg-orange-600 text-white' 
                        : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
                    }`}
                  >
                    <Laptop size={16} />
                    Browser
                  </button>
                  <button
                    onClick={() => setCallMode('phone')}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      callMode === 'phone' 
                        ? 'bg-cyan-600 text-white' 
                        : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
                    }`}
                  >
                    <Phone size={16} />
                    Twilio
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Manual Dial Section */}
        <div className="px-4 py-4 border-b border-gray-400/20 bg-gray-800/50">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">
            Dial a Number
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-500" />
              <input
                type="tel"
                placeholder="Enter phone number..."
                value={manualNumber}
                onChange={(e) => setManualNumber(e.target.value)}
                onKeyPress={handleManualKeyPress}
                className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors text-lg font-mono"
                data-testid="manual-dial-input"
              />
            </div>
            <button
              onClick={handleManualCall}
              disabled={!manualNumber.trim() || isCallingManual}
              className={`px-5 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                !manualNumber.trim() || isCallingManual
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
              data-testid="manual-call-btn"
            >
              {isCallingManual ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Phone size={20} />
              )}
              Call
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="px-4 py-3 border-b border-gray-400/20">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">
            Or Select from Contacts
          </label>
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search contacts by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
              autoFocus
              data-testid="new-call-search-input"
            />
          </div>
        </div>
        
        {/* Error Message */}
        {callError && (
          <div className="mx-4 mt-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{callError}</p>
          </div>
        )}

        {/* Contacts List */}
        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: '400px' }}>
          {loadingContacts && filteredContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={32} className="text-green-500 animate-spin mb-3" />
              <p className="text-gray-400 text-sm">Loading your iCloud contacts...</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Phone size={48} className="text-gray-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-400 mb-2">
                {searchQuery ? 'No contacts found' : 'No contacts with phone numbers'}
              </h3>
              <p className="text-sm text-gray-500">
                {searchQuery ? 'Try a different search term' : 'Sync your iCloud contacts to see them here'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredContacts.map((contact, index) => (
                <div
                  key={contact.id || index}
                  className={`bg-gray-800 border border-gray-400/20 rounded-lg p-4 hover:bg-gray-700 transition-colors ${
                    callingContact?.id === contact.id ? 'ring-2 ring-green-500' : ''
                  }`}
                  data-testid={`contact-${contact.id || index}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {contact.avatar ? (
                        <img
                          src={contact.avatar}
                          alt={contact.name}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-lg">
                            {contact.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{contact.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-400 truncate">{contact.phone || 'No phone number'}</p>
                        {contact.email && (
                          <p className="text-xs text-gray-500 truncate">{contact.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0 ml-3">
                      {contact.phone ? (
                        <button
                          onClick={() => handleCall(contact)}
                          disabled={callingContact !== null}
                          className={`p-3 rounded-full transition-colors flex items-center justify-center ${
                            callingContact?.id === contact.id
                              ? 'bg-green-500 animate-pulse'
                              : callingContact !== null
                              ? 'bg-gray-700 cursor-not-allowed opacity-50'
                              : 'bg-green-600 hover:bg-green-700'
                          }`}
                          title={callingContact?.id === contact.id ? 'Calling...' : 'Call'}
                          data-testid={`call-btn-${contact.id || index}`}
                        >
                          {callingContact?.id === contact.id ? (
                            <Loader2 size={20} className="text-white animate-spin" />
                          ) : (
                            <Phone size={20} className="text-white" />
                          )}
                        </button>
                      ) : (
                        <div className="p-3 bg-gray-700 rounded-full opacity-50" title="No phone number">
                          <Phone size={20} className="text-gray-500" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-400/20 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''} 
            {searchQuery && ` matching "${searchQuery}"`}
          </span>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-white font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewCallModal;
